/**
 * Runtime config — the leaf `EXPO_PUBLIC_*` values the flow reads, behind one accessor that
 * prefers consumer-supplied config and falls back to the env var.
 *
 * `babel-preset-expo` does not inline `EXPO_PUBLIC_*` across the `node_modules` boundary, so
 * inside a consumer's build every direct read is `undefined` — at any scope, which is why
 * moving a read into a function does not fix it. A consumer passes values through
 * `<OnboardingRoot config={…} />`; the standalone app passes nothing and the env fallbacks
 * apply.
 *
 * ```ts
 * getConfig('apiBaseUrl');        // string
 * getConfig('skipLegacySession'); // boolean
 * getConfig('transifexToken');    // string | undefined
 * ```
 *
 * **Vendor credentials are not here.** PostHog's and Datadog's keys live in
 * `VidaVendorConfig` (`#modules/vidaVendors`) — the adapter that constructs those clients —
 * and a host that injects its own `analytics` never states them. The `embedded` flag that
 * used to arbitrate between the two is gone with them. See
 * `docs/vendor-ownership-proposal.md`.
 *
 * **Read config through `getConfig`, never `process.env` directly.** A direct read compiles
 * and then silently resolves to `undefined` in a consumer, which is how requests end up at a
 * fallback URL.
 *
 * **Never call `getConfig` at module scope.** Config is published during `OnboardingRoot`'s
 * render, so anything read at import time sees an empty config. Three such reads had to be
 * undone before these resolvers were usable at all: `api/axiosConfig.ts` froze `baseURL` at
 * import (now resolved per request in an interceptor), `vidaVendors/posthog.ts` constructed
 * its client at import (now built by `configureVidaPostHog`), and
 * `addressVerification/api/lobApi.ts` baked its key into `axios.create` (now set per request).
 */
export { getConfig, setRuntimeConfig, resetRuntimeConfig, type ConfigKey } from './runtimeConfig';
export type { OnboardingConfig } from './types/onboardingConfig';
